import { LEVELS, levelById, MAX_RACERS, nextLevel, REACTION_SETS } from '@xox/shared'
import { ArrowLeft, Flag, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BoardOverlay } from '../components/BoardOverlay'
import { DPad } from '../components/DPad'
import { InvitePanel } from '../components/InvitePanel'
import { ReactionBar, ReactionOverlay } from '../components/Reactions'
import { isRoomGone, JoiningNotice, RoomChrome, RoomClosedCard } from '../components/RoomShell'
import { CountdownOverlay } from '../components/race/CountdownOverlay'
import { TurnBar } from '../components/TurnBar'
import { Card, IconButton, IconLink } from '../components/ui'
import { useAutoSeat } from '../hooks/useAutoSeat'
import { useConnectRoom } from '../hooks/useConnectRoom'
import { useDirectionInput } from '../hooks/useDirectionInput'
import { ordinal, plural } from '../lib/format'
import { iceServerNow, useIce } from '../store/ice'
import { useSession } from '../store/session'
import { IceBoard, type Puck } from './IceBoard'
import { RaceLobby, RacerLineup } from './RaceLobby'
import { RaceResults } from './RaceResults'
import { Standings } from './Standings'

export function RaceRoom() {
  const { code = '' } = useParams()
  const navigate = useNavigate()
  const me = useSession((s) => s.me)
  const room = useIce()
  const [pickedLevel, setPickedLevel] = useState(LEVELS[0]?.id ?? '')
  useConnectRoom(code, me?.playerId, useIce)

  const snap = room.snapshot
  // After a round, queue up the following level so "Next round" just continues the campaign.
  const phase = snap?.phase
  const playedLevelId = snap?.levelId
  useEffect(() => {
    if (phase !== 'results' || !playedLevelId) return
    setPickedLevel(nextLevel(playedLevelId)?.id ?? playedLevelId)
  }, [phase, playedLevelId])
  const mine = snap?.racers.find((r) => r.profile.id === room.playerId) ?? null
  useAutoSeat(snap !== null, mine !== null, room.takeSeat)
  const isHost = !!snap && snap.hostId === room.playerId
  const racing = snap?.phase === 'racing'
  const canMove = racing && !!mine && mine.finishedAt === null && room.status === 'open'
  const swipe = useDirectionInput(room.move, canMove)

  if (me && isRoomGone(room)) {
    return (
      <RoomClosedCard
        closedReason={room.closedReason === 'closed' ? 'closed' : 'expired'}
        noun="race"
        onBack={() => navigate('/ice')}
        backLabel="Back to Ice Ice Baby"
      />
    )
  }
  if (!snap) return <JoiningNotice label={`Joining race ${code}…`} />

  const level = snap.levelId ? levelById(snap.levelId) : undefined
  const boardLevel = level ?? levelById(pickedLevel) ?? LEVELS[0]!
  const pucks: Puck[] =
    snap.phase === 'lobby'
      ? []
      : snap.racers.map((r) => ({
          id: r.profile.id,
          pos: room.falling[r.profile.id] ?? r.pos,
          color: r.profile.color,
          ghost: r.profile.id !== room.playerId,
          label: r.profile.nickname,
          falling: !!room.falling[r.profile.id],
          finished: r.place !== null,
        }))
  const myRun =
    mine && snap.phase !== 'lobby'
      ? { pos: mine.pos, moves: mine.moves, cracked: mine.cracked, hasKey: mine.hasKey }
      : null

  const leave = () => {
    room.leave()
    navigate('/ice')
  }

  return (
    <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
      <RoomChrome error={room.error} onDismiss={room.dismissError} status={room.status} />

      <section className="flex flex-1 flex-col items-center gap-3" aria-label="Race">
        <div className="flex w-full max-w-[400px] items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate font-semibold">
            {snap.phase === 'lobby' && 'Lobby'}
            {snap.phase === 'countdown' && 'Get ready…'}
            {snap.phase === 'racing' && `Round ${snap.round} · ${level?.name ?? ''}`}
            {snap.phase === 'results' && `Round ${snap.round} results`}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-slate-500">
            {snap.racers.length}/{MAX_RACERS}
            {snap.spectators > 0 && ` · ${snap.spectators} 👀`}
            {mine ? (
              <IconButton label={isHost ? 'End race for everyone' : 'Leave race'} onClick={leave}>
                <LogOut className="size-4" aria-hidden />
              </IconButton>
            ) : (
              <IconLink to="/ice" label="Back to Ice Ice Baby">
                <ArrowLeft className="size-4" aria-hidden />
              </IconLink>
            )}
          </span>
        </div>

        {snap.phase === 'racing' && (
          <TurnBar
            deadlineAt={snap.deadlineAt}
            turnMs={snap.raceMs}
            mine={false}
            name="Race"
            label="Race"
            tone="neutral"
            serverNow={iceServerNow}
          />
        )}

        <div className="relative" {...(canMove ? swipe : {})}>
          <IceBoard
            level={boardLevel}
            pucks={pucks}
            run={myRun}
            doorReached={mine?.place !== null && mine?.place !== undefined}
            className={snap.phase === 'lobby' ? 'opacity-70' : ''}
          />
          {snap.phase === 'lobby' && (
            <BoardOverlay wide>
              <RaceLobby
                snap={snap}
                playerId={room.playerId}
                code={code}
                pickedLevel={pickedLevel}
                onPickLevel={setPickedLevel}
                onStart={() => room.start(pickedLevel)}
                onJoin={room.takeSeat}
              />
            </BoardOverlay>
          )}
          {snap.phase === 'countdown' && (
            <CountdownOverlay deadlineAt={snap.deadlineAt} serverNow={iceServerNow}>
              <RacerLineup racers={snap.racers} hostId={snap.hostId} me={room.playerId} onDark />
            </CountdownOverlay>
          )}
          {snap.phase === 'racing' && mine?.place && (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
              <span className="rounded-full bg-emerald-500 px-3 py-1 text-sm font-bold text-white shadow">
                <Flag className="mr-1 inline size-4" aria-hidden /> {ordinal(mine.place)} — waiting
                for the others
              </span>
            </div>
          )}
          {snap.phase === 'results' && (
            <BoardOverlay wide>
              <RaceResults
                snap={snap}
                playerId={room.playerId}
                pickedLevel={pickedLevel}
                onPickLevel={setPickedLevel}
                onNext={() => room.start(pickedLevel)}
              />
            </BoardOverlay>
          )}
          <ReactionOverlay reactions={room.reactions} lanes="sender" />
        </div>

        <p className="text-sm text-slate-500" aria-live="polite">
          {snap.phase === 'racing' && mine && mine.finishedAt === null
            ? `${plural(mine.moves, 'move')}${mine.falls ? ` · ${plural(mine.falls, 'fall')}` : ''}${mine.hasKey ? ' · 🔑' : ''} — arrow keys, WASD or swipe`
            : snap.phase === 'racing' && !mine
              ? 'You are watching the race'
              : ''}
        </p>
        {canMove && (
          <div className="sm:hidden">
            <DPad onMove={room.move} />
          </div>
        )}
        <ReactionBar set={REACTION_SETS.ice} onReact={room.react} />
        {snap.phase !== 'lobby' && (
          <div className="w-full max-w-[400px] lg:hidden">
            <Standings
              racers={snap.racers}
              scores={snap.scores}
              hostId={snap.hostId}
              me={room.playerId}
              layout="strip"
            />
          </div>
        )}
      </section>

      <aside className="hidden w-80 shrink-0 flex-col gap-3 lg:flex" aria-label="Racers">
        <Card className="flex flex-col gap-3">
          <h2 className="font-semibold">Racers</h2>
          <Standings
            racers={snap.racers}
            scores={snap.scores}
            hostId={snap.hostId}
            me={room.playerId}
            layout="list"
          />
        </Card>
        <Card className="flex flex-col gap-3">
          <h2 className="font-semibold">Share this race</h2>
          <InvitePanel code={code} path="/ice/r" game="ice" />
        </Card>
      </aside>
    </div>
  )
}
